<template>
  <app-page :title="title" :icon="icon" :class="cssClass">
    <template #header>
      <div class="d-flex align-items-center">
        <divisions-dropdown v-if="showDivisionsDropdown" />
        <b-button
          v-if="portalUpdatesJan2026Enabled"
          variant="primary"
          :class="showDivisionsDropdown ? 'ml-3' : ''"
          :disabled="isGenerating || !canDownloadComputed"
          title="Download"
          @click="handleDownloadClick"
        >
          <b-spinner v-if="isGenerating" small />
          <font-awesome-icon v-else :icon="['fad', 'download']" class="mr-2" />Download
        </b-button>
      </div>
    </template>
    <template #custom-content>
      <b-col ref="contentRef">
        <analytics-nav />
        <slot />
      </b-col>
      <subsection-download-modal
        v-if="hasSectionSpecs"
        :sections="downloadSections"
        :visible="showModal"
        @hidden="showModal = false"
      />
    </template>
  </app-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import AppPage from '@/components/AppPage.vue';
import SubsectionDownloadModal from '@/components/SubsectionDownloadModal.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useSubsectionDownload from '@/composables/useSubsectionDownload';
import AnalyticsNav from '@/pages/analyticsHomepage/components/AnalyticsNav.vue';
import DivisionsDropdown from '@/pages/analyticsHomepage/components/DivisionsDropdown.vue';

const props = defineProps({
  title: { type: String, required: true },
  icon: { type: String, required: true },
  cssClass: { type: String, default: '' },
  defaultLabel: { type: String, required: true },
  showDivisionsDropdown: { type: Boolean, default: true },
  inlineKpis: { type: Boolean, default: true },
  canDownload: { type: Function, default: null },
  getSheetConfig: { type: Function, default: null },
  /**
   * Section specs for the multi-section download modal. Each spec:
   *   { key, label, ref, [inlineKpis] }
   * When provided, the Download button opens the modal instead of generating
   * a single-sheet PDF. Each ref'd component should expose:
   *   - `hasData` (required): boolean — whether the section is selectable.
   *   - `getExportTable` (required for XLSX/CSV): `() => { headers, rows }`.
   *     Sections that omit this stay PDF-only in the modal.
   *   - `getPdfData` (optional): `() => SheetExtras` — PDF-specific extras
   *     such as `trendingBrands` or `topKpis`. See `SheetExtras` typedef in
   *     `useSubsectionDownload`.
   */
  sectionSpecs: { type: Array, default: () => [] },
});

const contentRef = ref(null);
const showModal = ref(false);
const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { isGenerating, downloadSheet } = useSubsectionDownload();

const hasSectionSpecs = computed(() => props.sectionSpecs.length > 0);

const canDownloadComputed = computed(() => {
  if (props.canDownload) {
    return props.canDownload();
  }
  if (hasSectionSpecs.value) {
    return props.sectionSpecs.some(spec => spec.ref?.value?.hasData);
  }
  return true;
});

const downloadSections = computed(() =>
  props.sectionSpecs
    .filter(spec => spec.ref?.value?.hasData)
    .map(spec => {
      const child = spec.ref.value;
      return {
        key: spec.key,
        label: spec.label,
        inlineKpis: spec.inlineKpis ?? true,
        getContainerEl: () => spec.ref.value?.$el,
        // Only attach getExportTable when the child actually exposes one, so the
        // modal can filter sections out of the Excel/CSV checklist (sections
        // without tabular data like benchmark cards stay PDF-only).
        ...(typeof child.getExportTable === 'function'
          ? { getExportTable: () => spec.ref.value?.getExportTable?.() }
          : {}),
        // Page-level extras from `getSheetConfig` (e.g. Summary snapshot KPIs)
        // are merged into every section here, but `downloadPdf` strips
        // `topKpis` from sheets after the first so they only appear once. See
        // `useSubsectionDownload.downloadPdf`.
        getPdfData: () => ({
          ...(props.getSheetConfig ? props.getSheetConfig() : {}),
          ...(spec.ref.value?.getPdfData?.() || {}),
        }),
      };
    }),
);

async function handleDownloadClick() {
  if (hasSectionSpecs.value) {
    showModal.value = true;
    return;
  }
  await downloadSheet({
    label: props.defaultLabel,
    getContainerEl: () => contentRef.value?.$el || contentRef.value,
    inlineKpis: props.inlineKpis,
    getPdfData: props.getSheetConfig || undefined,
  });
}
</script>
